import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { config } from '@static/js/env.config';
import { executeWithDeferredLibrary, HiddenLogger, validateStr } from '@static/js/utils';
import { datadogRum } from '@datadog/browser-rum';

type ConsoleLevel = 'log' | 'warn' | 'error' | 'info';

type LogActions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [level in ConsoleLevel]: (...args: any[]) => void;
};

export type Logger = Readonly<LogActions>;

const [setLogger, withLogger] = executeWithDeferredLibrary<Logger>();

const hiddenLogger = new HiddenLogger();

const LOG_LEVELS = Object.values(StatusType);

const WILD_CARD_BLOCKLIST = new Set<string>([
  'account',
  'accounts',
  'account-callback',
  'vendors',
  'vendor',
  'ecard',
  'shop',
  'sevicecenter',
  'shipping-policy',
  'return-policy',
  'examples',
  'eventredirect',
  'me'
]);

export const initDataDogLogs = () => {
  if (!config.datadogClientKey) {
    hiddenLogger.log('not initializing dd logs');
    return;
  }

  const url = new URL(window.location.href);

  datadogLogs.init({
    clientToken: config.datadogClientKey,
    service: 'joy-web',
    version: config.datadogVersion,
    env: config.joyEnvironment,
    sampleRate: 100, // Track logs from every session
    telemetrySampleRate: 20 // It's only for DD usage
  });

  datadogLogs.logger.setLevel(validateStr(url.searchParams.get('logLevel'), LOG_LEVELS, 'info'));

  setLogger(datadogLogs.logger);

  hiddenLogger.log('dd initialized');
};

export const initDataDogRum = () => {
  if (!config.datadogRumClientKey || !config.datadogRumApplicationId) {
    hiddenLogger.log('not initializing dd rum');
    return;
  }

  datadogRum.init({
    applicationId: config.datadogRumApplicationId,
    clientToken: config.datadogRumClientKey,
    service: 'joy-web',
    env: config.joyEnvironment,
    version: config.datadogVersion,
    sampleRate: 100,
    premiumSampleRate: 25, // Only 25% of the traffic will have its session recorded.
    trackInteractions: true,
    trackFrustrations: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: event => {
      const url = new URL(event.view.url);
      // ["", "handleLike", "subpath"]
      const pathsSplit = url.pathname.split('/');
      const [, possiblyHandle] = pathsSplit;
      if (!possiblyHandle || WILD_CARD_BLOCKLIST.has(possiblyHandle)) {
        return;
      }
      // if possiblyHandle exists, so does pathsSplit[1]
      pathsSplit[1] = '?';
      url.pathname = pathsSplit.join('/');
      url.searchParams.set('event-handle', possiblyHandle); // view events can't edit context data & so that's a hacky way to keep old handle reference.
      event.view.url = url.toString();
    }
  });

  datadogRum.startSessionReplayRecording();
  hiddenLogger.log('dd rum initialized');
};

const _consoleLevels: ConsoleLevel[] = ['log', 'warn', 'error', 'info'];
export const logger: Logger = _consoleLevels.reduce((acc, logLevel) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  acc[logLevel] = (...args: any[]) => {
    withLogger(logger => {
      hiddenLogger.log('Logger ', ...args);
      logger[logLevel]?.(...args);
    });
  };
  return acc;
}, {} as LogActions);
